import React, { useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import LoadingBackdrop from '../../components/common/LoadingBackdrop/LoadingBackdrop';
import { authService } from '../../services/apiService';
import './LoginPage.css';

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from
        ? `${location.state.from.pathname}${location.state.from.search || ''}`
        : '/connections';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // If the user is already authenticated, send them back to wherever they
    // were headed (e.g. a summary-report link), falling back to connections.
    if (authService.isAuthenticated()) {
        return <Navigate to={redirectTo} replace />;
    }

    const validateField = (field, value) => {
        if (!value.trim()) {
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
            return `${fieldName} is required`;
        }
        return '';
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        const emailError = validateField('email', email);
        if (emailError) {
            newErrors.email = emailError;
            isValid = false;
        }

        const passwordError = validateField('password', password);
        if (passwordError) {
            newErrors.password = passwordError;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleEmailChange = (value) => {
        setEmail(value);
        // Clear error when user starts typing
        if (errors.email) {
            setErrors(prev => ({ ...prev, email: '' }));
        }
    };

    const handlePasswordChange = (value) => {
        setPassword(value);
        // Clear error when user starts typing
        if (errors.password) {
            setErrors(prev => ({ ...prev, password: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Field is labeled "Email" in the UI but sent as "username" to the API.
            const response = await authService.login({
                username: email.trim(),
                password,
            });
            if (response) {
                // toast.success('Logged in successfully');
                navigate(redirectTo, { replace: true });
            }
        } catch (error) {
            if (error.message === 'invalid credentials') toast.error('Invalid email or password');
            // toast.error(error?.message || 'Login failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="login-page">
            {/* Loading Backdrop */}
            <LoadingBackdrop isLoading={isSubmitting} />

            <div className="login-card">
                <div className="login-brand">
                    <img src="/parity_logo.svg" alt="DB Parity" className="login-logo" />
                    <span className="login-brand-text">DB Parity</span>
                </div>

                <h1 className="login-title">LOGIN</h1>

                <form className="login-form" onSubmit={handleSubmit} noValidate>
                    <div className="login-form-group">
                        <div className={`login-field ${errors.email ? 'error' : ''}`}>
                            <Mail className="login-field-icon" />
                            <input
                                type="text"
                                className="login-input"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => handleEmailChange(e.target.value)}
                                autoComplete="username"
                                disabled={isSubmitting}
                            />
                        </div>
                        <ErrorMessage message={errors.email} />
                    </div>

                    <div className="login-form-group">
                        <div className={`login-field ${errors.password ? 'error' : ''}`}>
                            <Lock className="login-field-icon" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                className="login-input"
                                placeholder="Enter password"
                                value={password}
                                onChange={(e) => handlePasswordChange(e.target.value)}
                                autoComplete="current-password"
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                className="login-eye-btn"
                                onClick={() => setShowPassword((prev) => !prev)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                tabIndex={-1}
                            >
                                {showPassword ? <Eye /> : <EyeOff />}
                            </button>
                        </div>
                        <ErrorMessage message={errors.password} />
                    </div>

                    <button
                        type="submit"
                        className="login-submit-btn"
                        disabled={isSubmitting}
                    >
                        SIGN IN
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;